import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import { Modal, ModalBody } from "react-bootstrap";
import { useForm } from "react-hook-form";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import { CallingV2 } from "../../utils/APIClient";
import { triggerAlert } from "../../utils";
import { confirm } from "../../common/ConfirmDialog";

/**
 * Calling V2 → Knowledge Templates.
 *
 * Admin-curated KBs (owner_org=NULL) that AgentTemplates can reference via
 * default_kb_template. Documents uploaded here are shared content the runtime
 * indexes once and any agent can search.
 */
export default function CallingV2KnowledgeTemplates() {
  document.title = "Digital Employees — V2 Knowledge Templates";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);

  const [openKb, setOpenKb] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [openDocs, setOpenDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await CallingV2.listKBTemplates();
      const list = r?.data?.results || [];
      list.forEach((v, i) => { v.Num = i + 1; });
      setRows(list);
    } catch (e) {
      setRows([]);
      triggerAlert("error", "Oops...", "Failed to load knowledge templates.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openCreate = () => {
    setEditing(null);
    reset({ name: "", description: "" });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    reset({ name: row.name || "", description: row.description || "" });
    setShowModal(true);
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (editing) {
        await CallingV2.updateKBTemplate(editing.id, data);
        triggerAlert("success", "Saved", "KB updated.");
      } else {
        await CallingV2.createKBTemplate(data);
        triggerAlert("success", "Created", "Knowledge base created on the runtime.");
      }
      setShowModal(false);
      reset();
      fetchRows();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.detail || "Save failed.";
      triggerAlert("error", "Oops...", msg);
    } finally { setSubmitting(false); }
  };

  const onDelete = async (row) => {
    const ok = await confirm({
      title: "Delete knowledge base?",
      message: `Delete KB "${row.name}"? This removes it on the runtime too.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await CallingV2.deleteKBTemplate(row.id);
      triggerAlert("success", "Deleted", "KB removed.");
      fetchRows();
    } catch (e) {
      triggerAlert("error", "Oops...", "Delete failed.");
    }
  };

  const openKbDetail = async (row) => {
    setOpenKb(row);
    setOpenLoading(true);
    try {
      const r = await CallingV2.listKBTemplateDocs(row.id);
      setOpenDocs(r?.data?.results || []);
    } catch (e) {
      setOpenDocs([]);
    } finally { setOpenLoading(false); }
  };

  const refreshDocs = async () => {
    if (!openKb) return;
    try {
      const r = await CallingV2.listKBTemplateDocs(openKb.id);
      setOpenDocs(r?.data?.results || []);
    } catch (e) { /* swallow */ }
  };

  const onUploadDoc = async (e) => {
    e.preventDefault();
    if (!openKb || !fileRef.current?.files?.[0]) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileRef.current.files[0]);
      await CallingV2.uploadKBTemplateDoc(openKb.id, fd);
      triggerAlert("success", "Uploaded", "Document submitted for ingest.");
      if (fileRef.current) fileRef.current.value = "";
      refreshDocs();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.detail || "Upload failed.";
      triggerAlert("error", "Oops...", msg);
    } finally { setUploading(false); }
  };

  const onDeleteDoc = async (doc) => {
    if (!openKb) return;
    const ok = await confirm({
      title: "Delete document?",
      message: `Delete "${doc.filename}"?`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await CallingV2.deleteKBTemplateDoc(openKb.id, doc.id);
      triggerAlert("success", "Deleted", "Document removed.");
      refreshDocs();
    } catch (e) {
      triggerAlert("error", "Oops...", "Delete failed.");
    }
  };

  const columns = [
    { name: "S.No", width: "70px", selector: (r) => r.Num, cell: (r) => <span>{r.Num}</span> },
    { name: "Name", sortable: true, selector: (r) => r.name, cell: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { name: "Description", sortable: true, selector: (r) => r.description, cell: (r) => <span className="text-muted small">{r.description || "—"}</span> },
    { name: "Documents", sortable: true, selector: (r) => r.doc_count, width: "130px", cell: (r) => <span>{r.doc_count}</span> },
    {
      name: "Runtime ID", sortable: true, selector: (r) => r.runtime_kb_id,
      cell: (r) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r.runtime_kb_id}</span>,
    },
    {
      name: "Action",
      cell: (r) => (
        <div className="d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => openKbDetail(r)}>Open</button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => openEdit(r)}>Edit</button>
          <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(r)}>Delete</button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: "#ffffff", borderRadius: "8px" } },
    headRow: { style: { backgroundColor: "#064c77", borderBottom: "1px solid #dee2e6", minHeight: "52px" } },
    headCells: { style: { color: "#ffffff", fontSize: "13px", fontWeight: "700", textTransform: "uppercase" } },
    rows: { style: { minHeight: "60px", backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#f8f9fa" } } },
    cells: { style: { fontSize: "14px", color: "#495057", borderBottom: "1px solid #f1f3f5" } },
    pagination: { style: { borderTop: "1px solid #dee2e6", padding: "8px" } },
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading="Knowledge Templates" heading="Calling V2 / Knowledge Templates" />
            <div className="row"><div className="col-12"><div className="card"><div className="card-body">
              <div className="alert alert-info py-2 mb-3">
                Admin-curated knowledge bases (FAQ packs, product manuals, etc.). Each
                AgentTemplate can reference one as its default KB so customer-cloned
                agents inherit it.
              </div>
              <div className="row align-items-center mb-3">
                <div className="col-md-8"></div>
                <div className="col-md-4 text-end">
                  <button className="btn btn-primary w-md btn-md" onClick={openCreate}>+ New Knowledge Base</button>
                </div>
              </div>
              {!loading ? (
                <DataTable columns={columns} data={rows} pagination fixedHeader fixedHeaderScrollHeight="600px" persistTableHead customStyles={customStyles}
                  noDataComponent={<div style={{ padding: 24 }}>No knowledge templates yet.</div>} />
              ) : <div className="loader-overlay text-white"><Loader /></div>}
            </div></div></div></div>
          </div>
        </div>
        <Footer />
      </div>

      {/* Create/Edit modal */}
      <Modal size="md" show={showModal} onHide={() => !submitting && setShowModal(false)} backdrop="static" keyboard={!submitting}>
        <Modal.Header className="modal-header_box bg-my-primary p-3 d-flex justify-content-between" closeButton>
          <h1 className="modal-title fs-5">{editing ? "Edit Knowledge Base" : "New Knowledge Base"}</h1>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <div className="mb-3">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input className="form-control" {...register("name", { required: "Required" })} />
              {errors.name && <div className="text-danger font_14 mt-1">{errors.name.message}</div>}
            </div>
            <div className="mb-2">
              <label className="form-label">Description</label>
              <textarea className="form-control" rows="3" {...register("description")} />
            </div>
          </ModalBody>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? "Saving..." : "Save"}</button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* KB detail / docs */}
      <Modal size="lg" show={!!openKb} onHide={() => !uploading && setOpenKb(null)} backdrop="static" keyboard={!uploading}>
        {openKb && (
          <>
            <Modal.Header className="modal-header_box bg-my-primary p-3 d-flex justify-content-between" closeButton>
              <h1 className="modal-title fs-5">{openKb.name}</h1>
            </Modal.Header>
            <ModalBody>
              <p className="text-muted">{openKb.description || <em>No description.</em>}</p>
              <p className="text-muted small">Runtime KB ID: <code>{openKb.runtime_kb_id}</code></p>

              <hr />
              <h6 className="mb-3">Upload a document</h6>
              <form onSubmit={onUploadDoc} className="d-flex gap-2 align-items-center mb-3">
                <input type="file" ref={fileRef} className="form-control"
                  accept=".pdf,.txt,.md,.docx,.html,.htm,.csv" />
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </form>
              <p className="text-muted small">Supported: PDF, TXT, MD, DOCX, HTML, CSV — up to 20MB.</p>

              <hr />
              <h6 className="mb-2">Documents ({openDocs.length})</h6>
              {openLoading ? <Loader /> : openDocs.length === 0 ? (
                <p className="text-muted">No documents yet.</p>
              ) : (
                <table className="table table-sm">
                  <thead><tr><th>File</th><th>Size</th><th>Status</th><th>Uploaded</th><th></th></tr></thead>
                  <tbody>
                    {openDocs.map((d) => (
                      <tr key={d.id}>
                        <td><span style={{ fontFamily: "monospace", fontSize: 13 }}>{d.filename}</span></td>
                        <td>{d.size_bytes ? `${Math.round(d.size_bytes / 1024)} KB` : "—"}</td>
                        <td>
                          {d.status === "ingested"
                            ? <span className="badge bg-success">ingested</span>
                            : d.status === "failed"
                              ? <span className="badge bg-danger" title={d.error_message}>failed</span>
                              : <span className="badge bg-warning text-dark">{d.status}</span>}
                        </td>
                        <td className="small text-muted">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : "—"}</td>
                        <td><button className="btn btn-outline-danger btn-sm" onClick={() => onDeleteDoc(d)}>Delete</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ModalBody>
            <Modal.Footer>
              <button className="btn btn-light" onClick={refreshDocs}>Refresh</button>
              <button className="btn btn-primary" onClick={() => setOpenKb(null)}>Close</button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
}
